import { useCallback, useEffect, useMemo, useRef, useState, type SyntheticEvent } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import PlayArrow from '@mui/icons-material/PlayArrow';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type {
  FlowControlPerformMode,
  OutputPreviewTab,
  SimulationCueSheetRow,
} from '@/types/player/simulation-cue-sheet.types';
import { SimulationOutputPreview } from '@/components/preview/simulation-output-preview';
import { LiveKitOutputConnector } from '@/components/preview/livekit-output-connector';
import { useSimulationPreviewLayersMutation } from '@/state/mutations/player/use-simulation-preview-layers';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslations } from '@/i18n/use-translations';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useFlowControlLiveNextShowModeStore } from '@/state/stores/use-flow-control-live-next-show-mode-store';
import {
  resolveCurrentCueSheetPointer,
  resolveLiveNextCueTarget,
  resolveLookAheadPrefetchRow,
  resolveNextCuePreviewRow,
  simulationRowFromLiveNextTarget,
} from '@/helpers/resolve-next-cue-preview-row.helper';
import { getObjectId } from '@/helpers/object.id.helper';
import { getOnAirLivekitRoomIdFromFlow, resolveSocketEntityFromFlow } from '@/helpers/entity-ref.helper';
import { usePerformAdvanceCue } from '@/hooks/use-perform-advance-cue';
import { useNextCueRecalculating } from '@/hooks/use-next-cue-recalculating';
import { useGetPlayersQuery } from '@/state/queries/players/use-get-players';
import { FlowControlPreviewFrameLabel } from './flow-control-preview-frame-label';
import { PerformShowModeOverlay } from '@/components/flows/detail/edit-details/perform-view/perform-show-mode-overlay';
import { FlowControlPreviewFrame } from './flow-control-preview-frame';
import { FlowControlNextCueMetaOverlay } from './flow-control-next-cue-meta-overlay';
import { FlowControlNextStaticPreviewDisclaimer } from './flow-control-next-static-preview-disclaimer';
import { resolveNextCuePreviewMeta } from '@/helpers/resolve-next-cue-preview-meta.helper';
import { LiveSlideControlProvider } from '@/components/preview/live-slide-control.context';
import type { OutputLayer } from '@/components/preview/output-layer-preview';
import type { PresentationSlideNavigationScope } from '@/components/preview/preview-presentation-slides-layer.types';
import { liveCueSheetToSimulationRows } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import { useCueTransportBusyStore } from '@/state/player/cue-transport-busy.store';
import { useNextCueRecalculatingStore } from '@/state/player/next-cue-recalculating.store';
import { previewLayersMediaFingerprint } from '@/helpers/preview/blob-asset-key.helper';
import {
  attachLiveKitTracksToLookAheadPreviewLayers,
  isSequentialLookAheadPromotePending,
  lookAheadLayersUsableForNextPreview,
  previewLayersUseFailedImageUrl,
  resolveAlreadyShownNextDeckLayers,
  lookAheadMetaMatchesNextTarget,
  nextPreviewLayersMatchTarget,
  PLAYER_TYPE_BY_TAB,
  resolveLookAhead2PreviewLayers,
  resolveLookAheadPreviewLayers,
  resolveLookAheadPreviewMeta,
  resolveLookAheadResumePreviewLayers,
  filterOutputLayersToCueItem,
  resolveOutputPreviewLayers,
} from '@/helpers/resolve-output-preview-layers';
import { seedLiveSlideIndexFromLayerCurrentIndex } from '@/helpers/resolve-start-slide-index-for-take.helper';
import { resolveNextShowModeFromPreselect } from '@/helpers/media-playlist-next-show-mode.helper';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import {
  buildNextCuePreviewRequestKey,
  livePictureHasRoomCamera,
  lookAheadPushMatchesHeldShowMode,
  previewLayersFitShowMode,
  parseNextCuePreviewRequestKey,
  resolveLiveNextPreviewHttpDecision,
  resolveLiveNextPreviewHttpShowMode,
  shouldBlankLiveNextPreview,
  shouldSkipLiveNextPreviewHttpFallback,
} from '@/helpers/preview/next-cue-preview-request.helper';
import { warmPreviewLayerMedia } from '@/helpers/preview/warm-preview-layer-media.helper';
import { PreviewLayersCache } from '@/helpers/preview/preview-layers-cache.helper';

type PreviewShowMode = 'mediaOnly' | 'both' | 'speakerOnly';

function normalizePreviewShowMode(showMode?: string): PreviewShowMode {
  if (showMode === 'both' || showMode === 'mixedView') {
    return 'both';
  }
  if (showMode === 'speakerOnly') {
    return 'speakerOnly';
  }
  return 'mediaOnly';
}

export interface FlowControlNextCuePreviewProps {
  flow: FlowDto;
  performMode: FlowControlPerformMode;
  liveContextState?: LiveContextStateDto;
  planFlowId?: string;
  entityRef: string;
  liveContextQueryUpdatedAt?: number;
  /** Shared Stage / Broadcast tab from Now/Next stack. */
  tab?: OutputPreviewTab;
  showLabel?: boolean;
  /** When false, both Now and Next are still loading — show spinner instead of Play. */
  viewsReady?: boolean;
  onReadyChange?: (ready: boolean) => void;
  /** Stable Next-target identity for parent Play latch (item + slot + scope). */
  onCueIdentityChange?: (identity: string) => void;
  /**
   * Item id whose picture is on screen, {@link LIVE_NEXT_PAINTED_NONE} when the live cue
   * has settled with nothing upcoming, or null while Next is still loading.
   */
  onLiveNextPainted?: (itemId: string | null) => void;
}

/** Live cue has a current step and no upcoming item. Not the same as null, which is still loading. */
export const LIVE_NEXT_PAINTED_NONE = '';

export function FlowControlNextCuePreview({
  flow,
  performMode,
  liveContextState: liveContextStateFromGet,
  planFlowId: planFlowIdProp,
  entityRef,
  liveContextQueryUpdatedAt,
  tab = 'stageRoom',
  showLabel = true,
  viewsReady = true,
  onReadyChange,
  onCueIdentityChange,
  onLiveNextPainted,
}: FlowControlNextCuePreviewProps) {
  const t = useTranslations();
  const liveContextState = useLiveContextStateWithLivePlayState(entityRef, liveContextStateFromGet);
  const planFlowId = planFlowIdProp ?? (flow.origin ? getObjectId(flow.origin) : flow.id);
  const livekitRoomId = getOnAirLivekitRoomIdFromFlow(planFlowId ?? '', flow.liveContext);
  const socketEntity = useMemo(() => resolveSocketEntityFromFlow(flow), [flow]);
  const previewPlayerType = PLAYER_TYPE_BY_TAB[tab];
  const { data: connectedPlayers } = useGetPlayersQuery(
    { entityId: socketEntity.entityId, entityType: socketEntity.entityType },
    { enabled: performMode === 'play' },
  );
  const displayPlayerIds = useMemo(() => {
    if (!connectedPlayers) {
      return undefined;
    }
    return connectedPlayers.filter((p) => p.type === previewPlayerType).map((p) => p.id);
  }, [connectedPlayers, previewPlayerType]);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationOverridePointer = useFlowControlSimulationCueStore((s) => s.overridePointer);
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);
  const playedReturnPreviousKeys = useFlowControlSimulationCueStore((s) => s.playedReturnPreviousKeys);
  const simulationNextShowMode = useFlowControlSimulationCueStore((s) => s.nextShowMode);
  const simulationOnAirShowMode = useFlowControlSimulationCueStore((s) => s.onAirShowMode);
  const setNextShowMode = useFlowControlSimulationCueStore((s) => s.setNextShowMode);
  const heldLiveNextShowMode = useFlowControlLiveNextShowModeStore((s) => s.showMode);
  const syncLiveNextShowModeItemId = useFlowControlLiveNextShowModeStore((s) => s.syncNextItemId);
  const appliedNextShowModeIdentityRef = useRef('');

  const mergedCueState = useLiveContextCueState(
    performMode === 'play' ? entityRef : undefined,
    undefined,
    liveContextState?.cueState,
  );
  const cueState = performMode === 'play' ? (mergedCueState ?? liveContextState?.cueState) : liveContextState?.cueState;

  useEffect(() => {
    if (performMode !== 'play') {
      return;
    }
    syncLiveNextShowModeItemId(cueState?.nextItemId);
  }, [performMode, cueState?.nextItemId, syncLiveNextShowModeItemId]);

  const predecessorShowMode = useMemo((): PreviewShowMode => {
    if (performMode === 'simulation') {
      return normalizePreviewShowMode(simulationOnAirShowMode);
    }
    return normalizePreviewShowMode(liveContextState?.playState?.showMode);
  }, [performMode, simulationOnAirShowMode, liveContextState?.playState?.showMode]);

  const currentPointer = useMemo(
    () =>
      resolveCurrentCueSheetPointer(simulationRows, performMode, simulationPointer, cueState, {
        simulationOverridePointer,
      }),
    [simulationRows, performMode, simulationPointer, simulationOverridePointer, cueState],
  );

  const liveNextTarget = useMemo(
    () =>
      performMode === 'play'
        ? resolveLiveNextCueTarget(cueState, liveContextState?.playState?.lookAheadInsertResume)
        : null,
    [performMode, cueState, liveContextState?.playState?.lookAheadInsertResume],
  );

  const nextRecalculating = useNextCueRecalculating(performMode === 'play' ? entityRef : undefined, cueState);
  const jumpLatch = useNextCueRecalculatingStore((s) =>
    performMode === 'play' && entityRef ? (s.latchByEntityRef[entityRef] ?? null) : null,
  );
  const transportBusy = useCueTransportBusyStore((s) =>
    performMode === 'play' && entityRef ? Boolean(s.latchByEntityRef[entityRef]?.active) : false,
  );
  const liveSheetRoomKey = entityRoomKeyFromEntityRef(entityRef);
  const wsCueSheet = usePerformControlWsStore((s) =>
    liveSheetRoomKey ? s.cueSheetByRoom[liveSheetRoomKey] : undefined,
  );
  const liveCueSheet = wsCueSheet ?? liveContextState?.cueSheet;

  /** Simulation: local sheet walk. LIVE: BE projection (sim row only for render metadata). */
  const nextRow = useMemo((): SimulationCueSheetRow | null => {
    if (performMode === 'simulation') {
      return resolveNextCuePreviewRow(simulationRows, currentPointer, {
        cueOverrideActive: simulationOverridePointer != null,
        resumePointer: simulationPointer,
        selectedItemIds: simulationSelectedItemIds,
        flow,
        playedReturnPreviousKeys,
      });
    }

    const liveSheetRows = liveCueSheet ? liveCueSheetToSimulationRows(liveCueSheet) : [];
    const rows = liveSheetRows.length ? liveSheetRows : simulationRows;
    if (nextRecalculating) {
      return simulationRowFromLiveNextTarget(jumpLatch?.expectedNext, rows, cueState?.pointer);
    }
    return simulationRowFromLiveNextTarget(liveNextTarget, rows, cueState?.pointer);
  }, [
    performMode,
    simulationRows,
    currentPointer,
    simulationOverridePointer,
    simulationPointer,
    simulationSelectedItemIds,
    playedReturnPreviousKeys,
    flow,
    liveNextTarget,
    nextRecalculating,
    jumpLatch?.expectedNext,
    liveCueSheet,
    cueState?.pointer,
  ]);

  const previewLayersMutation = useSimulationPreviewLayersMutation();
  const [layers, setLayers] = useState<OutputLayer[]>([]);
  const [layersRequestReady, setLayersRequestReady] = useState(true);
  const [contentReady, setContentReady] = useState(false);
  const [stillFrameResetNonce, setStillFrameResetNonce] = useState(0);
  const [failedImageUrls, setFailedImageUrls] = useState<string[]>([]);
  const imageRefetchUrlsRef = useRef(new Set<string>());

  const cueIdentity = useMemo(() => {
    // While jumping, cueState next is still the previous item — use the latch row.
    const itemId = nextRecalculating ? nextRow?.itemId : (liveNextTarget?.itemId ?? nextRow?.itemId);
    if (performMode === 'play' && itemId) {
      const slotId = nextRecalculating ? nextRow?.slotId : (liveNextTarget?.slotId ?? nextRow?.slotId);
      const scope = nextRecalculating ? nextRow?.scope : (liveNextTarget?.scope ?? nextRow?.scope);
      return `${itemId}|${slotId ?? ''}|${scope ?? ''}`;
    }
    if (!nextRow?.itemId) {
      return '';
    }
    return `${nextRow.itemId}|${nextRow.slotId ?? ''}|${nextRow.scope ?? ''}`;
  }, [
    performMode,
    nextRecalculating,
    liveNextTarget?.itemId,
    liveNextTarget?.slotId,
    liveNextTarget?.scope,
    nextRow?.itemId,
    nextRow?.slotId,
    nextRow?.scope,
  ]);

  const showMode = useMemo((): PreviewShowMode => {
    if (performMode === 'simulation') {
      return normalizePreviewShowMode(simulationNextShowMode);
    }
    // Operator hold always wins until syncNextItemId clears it on real Next advance.
    if (heldLiveNextShowMode != null) {
      return heldLiveNextShowMode;
    }
    return normalizePreviewShowMode(cueState?.lookAheadShowMode ?? liveContextState?.stagedSelection?.showMode);
  }, [
    performMode,
    simulationNextShowMode,
    heldLiveNextShowMode,
    cueState?.lookAheadShowMode,
    liveContextState?.stagedSelection?.showMode,
  ]);

  const nextIsPresentation = useMemo(() => {
    if (!nextRow) {
      return false;
    }
    return resolveNextCuePreviewMeta({ row: nextRow, flow, slots: flow.slots, t }).isPresentation;
  }, [nextRow, flow, t]);

  const previewHttpShowMode = useMemo(() => {
    // Stage/Room Next is the media. Show mode only changes the broadcast composition.
    if (performMode === 'play' && tab !== 'live') {
      return 'mediaOnly' as const;
    }
    if (performMode === 'play') {
      return resolveLiveNextPreviewHttpShowMode({ showMode, isPresentation: nextIsPresentation });
    }
    return showMode;
  }, [performMode, showMode, nextIsPresentation, tab]);

  const roomCameraOnAir = useMemo(
    () => livePictureHasRoomCamera(liveContextState?.playState?.byPlayer),
    [liveContextState?.playState?.byPlayer],
  );

  const nextRemoteSpeakerIds = useMemo(() => {
    if (liveContextState?.remoteSpeakersOnStage !== true) {
      return [];
    }
    // Room focus keeps the speakers when the live picture has no room camera to fall back from.
    if (liveContextState.focusMode === 'room' && roomCameraOnAir) {
      return [];
    }
    const nextSlotId = nextRow?.slotId;
    if (!nextSlotId || nextSlotId !== cueState?.activeSlotId) {
      return [];
    }
    return (liveContextState.stagedSelection?.selectedItemIds ?? []).filter((id) => id.startsWith('remoteSpeaker:'));
  }, [
    liveContextState?.remoteSpeakersOnStage,
    liveContextState?.focusMode,
    liveContextState?.stagedSelection?.selectedItemIds,
    nextRow?.slotId,
    cueState?.activeSlotId,
    roomCameraOnAir,
  ]);

  const previewRequestKey = useMemo(
    () =>
      buildNextCuePreviewRequestKey({
        planFlowId,
        tab,
        itemId: nextRow?.itemId,
        slotId: nextRow?.slotId,
        scope: nextRow?.scope,
        showMode: previewHttpShowMode,
        remoteSpeakerIds: nextRemoteSpeakerIds,
        focusMode: liveContextState?.focusMode,
      }),
    [
      planFlowId,
      nextRow?.itemId,
      nextRow?.slotId,
      nextRow?.scope,
      tab,
      previewHttpShowMode,
      nextRemoteSpeakerIds,
      liveContextState?.focusMode,
    ],
  );

  /**
   * LIVE: BE-pushed `_lookahead:*` layers matching cue Next. When present, skip simulation-preview HTTP.
   * Empty / non-renderable / wrong-item snapshots must not block HTTP fallback (blank Next).
   */
  const pushedLookAheadLayers = useMemo((): OutputLayer[] | null => {
    if (performMode !== 'play') {
      return null;
    }
    const nextItemId = nextRecalculating
      ? (nextRow?.itemId ?? liveNextTarget?.itemId)
      : (liveNextTarget?.itemId ?? nextRow?.itemId);
    const nextIdentity = {
      itemId: nextItemId,
      slotId: nextRecalculating ? nextRow?.slotId : (liveNextTarget?.slotId ?? nextRow?.slotId),
      scope: nextRecalculating ? nextRow?.scope : (liveNextTarget?.scope ?? nextRow?.scope),
    };
    if (nextItemId) {
      const resumeMeta = liveContextState?.playState?.lookAheadInsertResume;
      if (
        lookAheadMetaMatchesNextTarget(resumeMeta, nextIdentity) &&
        lookAheadPushMatchesHeldShowMode({
          metaShowMode: resumeMeta?.showMode,
          heldShowMode: showMode,
        })
      ) {
        const resumeLayers = filterOutputLayersToCueItem(
          resolveLookAheadResumePreviewLayers(liveContextState, tab),
          nextItemId,
        );
        if (
          lookAheadLayersUsableForNextPreview(resumeLayers, nextItemId) &&
          (tab !== 'live' || previewLayersFitShowMode(resumeLayers, showMode)) &&
          !previewLayersUseFailedImageUrl(resumeLayers, failedImageUrls)
        ) {
          return resumeLayers;
        }
      }
    }
    const nextPlus1 = liveContextState?.playState?.lookAheadPreview2;
    if (
      nextItemId &&
      lookAheadMetaMatchesNextTarget(nextPlus1, nextIdentity) &&
      lookAheadPushMatchesHeldShowMode({
        metaShowMode: nextPlus1?.showMode,
        heldShowMode: showMode,
      })
    ) {
      const nextPlus1Layers = filterOutputLayersToCueItem(
        resolveLookAhead2PreviewLayers(liveContextState, tab),
        nextItemId,
      );
      if (
        lookAheadLayersUsableForNextPreview(nextPlus1Layers, nextItemId) &&
        (tab !== 'live' || previewLayersFitShowMode(nextPlus1Layers, showMode)) &&
        !previewLayersUseFailedImageUrl(nextPlus1Layers, failedImageUrls)
      ) {
        return nextPlus1Layers;
      }
    }
    const meta = resolveLookAheadPreviewMeta(liveContextState);
    const lookAheadMatches =
      lookAheadMetaMatchesNextTarget(meta, nextIdentity) &&
      lookAheadPushMatchesHeldShowMode({
        metaShowMode: meta?.showMode,
        heldShowMode: showMode,
      });
    if (lookAheadMatches) {
      const layers = resolveLookAheadPreviewLayers(liveContextState, tab);
      if (
        nextItemId &&
        lookAheadLayersUsableForNextPreview(layers, nextItemId) &&
        (tab !== 'live' || previewLayersFitShowMode(layers, showMode)) &&
        !previewLayersUseFailedImageUrl(layers, failedImageUrls)
      ) {
        return layers;
      }
    }
    const alreadyShown = nextItemId
      ? resolveAlreadyShownNextDeckLayers(liveContextState, nextItemId, { tab, displayPlayerIds })
      : null;
    if (
      alreadyShown &&
      (tab !== 'live' || previewLayersFitShowMode(alreadyShown, showMode)) &&
      !previewLayersUseFailedImageUrl(alreadyShown, failedImageUrls)
    ) {
      return alreadyShown;
    }
    return null;
  }, [
    performMode,
    liveContextState,
    liveNextTarget?.itemId,
    liveNextTarget?.slotId,
    liveNextTarget?.scope,
    nextRow?.itemId,
    nextRow?.slotId,
    nextRow?.scope,
    nextRecalculating,
    tab,
    displayPlayerIds,
    showMode,
    failedImageUrls,
  ]);

  const usePushedLookAhead = pushedLookAheadLayers != null;
  const sequentialPromotePending =
    !nextRecalculating &&
    isSequentialLookAheadPromotePending({
      next: {
        itemId: liveNextTarget?.itemId ?? nextRow?.itemId,
        slotId: liveNextTarget?.slotId ?? nextRow?.slotId,
        scope: liveNextTarget?.scope ?? nextRow?.scope,
      },
      lookAhead: resolveLookAheadPreviewMeta(liveContextState),
      lookAhead2: liveContextState?.playState?.lookAheadPreview2,
    });
  const skipLiveHttpFallback = shouldSkipLiveNextPreviewHttpFallback({
    performMode,
    sequentialPromotePending,
    transportBusy,
    nextRecalculating,
  });

  /** Simulation only: row that becomes Next after the upcoming take. */
  const prefetchRow = useMemo(
    () =>
      performMode === 'simulation'
        ? resolveLookAheadPrefetchRow(simulationRows, nextRow, {
            selectedItemIds: simulationSelectedItemIds,
            flow,
            playedReturnPreviousKeys,
          })
        : null,
    [simulationRows, nextRow, performMode, simulationSelectedItemIds, playedReturnPreviousKeys, flow],
  );

  const prefetchRequestKey = useMemo(
    () =>
      buildNextCuePreviewRequestKey({
        planFlowId,
        tab,
        itemId: prefetchRow?.itemId,
        slotId: prefetchRow?.slotId,
        scope: prefetchRow?.scope,
        showMode,
      }),
    [planFlowId, prefetchRow?.itemId, prefetchRow?.slotId, prefetchRow?.scope, tab, showMode],
  );

  const debouncedPreviewRequestKey = useDebounce(previewRequestKey, 300);
  const layersCacheRef = useRef(new PreviewLayersCache());
  const inFlightRequestsRef = useRef(new Map<string, Promise<OutputLayer[]>>());
  const appliedRequestKeyRef = useRef('');
  const appliedShowModeRef = useRef(showMode);
  const layersFingerprintRef = useRef('');
  const layersRef = useRef(layers);
  const previewRequestKeyRef = useRef(previewRequestKey);
  const usePushedLookAheadRef = useRef(usePushedLookAhead);
  layersRef.current = layers;
  useEffect(() => {
    previewRequestKeyRef.current = previewRequestKey;
  }, [previewRequestKey]);
  useEffect(() => {
    usePushedLookAheadRef.current = usePushedLookAhead;
  }, [usePushedLookAhead]);

  useEffect(() => {
    onCueIdentityChange?.(cueIdentity);
  }, [cueIdentity, onCueIdentityChange]);

  useEffect(() => {
    imageRefetchUrlsRef.current.clear();
    setFailedImageUrls([]);
  }, [cueIdentity]);

  const nextRowShowMode = nextRow?.nextShowMode;

  /**
   * Simulation only: apply per-item nextShowMode when Next identity / preselect changes.
   * LIVE look-ahead is BE-owned (cueState.lookAheadShowMode).
   */
  useEffect(() => {
    if (performMode !== 'simulation' || !cueIdentity) {
      return;
    }

    const resolved = resolveNextShowModeFromPreselect(nextRowShowMode, predecessorShowMode);
    const applyKey = `${cueIdentity}|${nextRowShowMode ?? 'inherit'}|${resolved}|${predecessorShowMode}`;
    if (appliedNextShowModeIdentityRef.current === applyKey) {
      return;
    }
    appliedNextShowModeIdentityRef.current = applyKey;

    const current = useFlowControlSimulationCueStore.getState().nextShowMode;
    if (current !== resolved) {
      setNextShowMode(resolved);
    }
  }, [cueIdentity, nextRowShowMode, performMode, predecessorShowMode, setNextShowMode]);

  const applyLayers = useCallback((requestKey: string, nextLayers: OutputLayer[]) => {
    const nextFingerprint = previewLayersMediaFingerprint(nextLayers);
    const sameMedia = layersFingerprintRef.current === nextFingerprint && nextFingerprint.length > 0;
    appliedRequestKeyRef.current = requestKey;
    layersFingerprintRef.current = nextFingerprint;
    setLayers(nextLayers);
    setLayersRequestReady(true);
    if (!sameMedia) {
      setStillFrameResetNonce((n) => n + 1);
      setContentReady(nextLayers.length === 0);
    }
  }, []);

  const fetchPreviewLayers = useCallback(
    (requestKey: string): Promise<OutputLayer[]> => {
      const inFlight = inFlightRequestsRef.current.get(requestKey);
      if (inFlight) {
        return inFlight;
      }
      const request = parseNextCuePreviewRequestKey(requestKey);
      if (!request) {
        return Promise.resolve([]);
      }
      const pending = previewLayersMutation
        .mutateAsync(request)
        .then((result) => {
          layersCacheRef.current.set(requestKey, result.layers);
          return result.layers;
        })
        .finally(() => {
          inFlightRequestsRef.current.delete(requestKey);
        });
      inFlightRequestsRef.current.set(requestKey, pending);
      return pending;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutateAsync identity is stable enough for debounced preview
    [],
  );

  const loadPreviewLayersForKey = useCallback(
    (requestKey: string) => {
      const cached = layersCacheRef.current.get(requestKey);
      if (cached) {
        applyLayers(requestKey, cached);
        return;
      }
      void fetchPreviewLayers(requestKey)
        .then((fetched) => {
          if (previewRequestKeyRef.current !== requestKey || usePushedLookAheadRef.current) {
            return;
          }
          applyLayers(requestKey, fetched);
        })
        .catch(() => {
          if (previewRequestKeyRef.current === requestKey) {
            setLayersRequestReady(true);
          }
        });
    },
    [applyLayers, fetchPreviewLayers],
  );

  const handlePreviewImageError = (event: SyntheticEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLImageElement)) {
      return;
    }
    const url = target.getAttribute('src') || '';
    if (!url || imageRefetchUrlsRef.current.has(url)) {
      return;
    }
    imageRefetchUrlsRef.current.add(url);
    layersCacheRef.current.delete(previewRequestKeyRef.current);
    appliedRequestKeyRef.current = '';
    setFailedImageUrls((prev) => (prev.includes(url) ? prev : [...prev, url]));
    // Pushed look-ahead drops itself when this URL is rejected, and that effect refetches.
    // An HTTP or simulation picture has no push, so reload it here. Clear the fingerprint so
    // the same URL still remounts after a fresh response.
    const requestKey = previewRequestKeyRef.current;
    if (!usePushedLookAheadRef.current && requestKey) {
      layersFingerprintRef.current = '';
      setLayersRequestReady(false);
      loadPreviewLayersForKey(requestKey);
    }
  };

  /** LIVE: paint BE-pushed look-ahead as soon as identity matches — no keep-previous, no debounce. */
  useEffect(() => {
    if (!usePushedLookAhead || !previewRequestKey || !pushedLookAheadLayers) {
      return;
    }
    applyLayers(previewRequestKey, pushedLookAheadLayers);
    // BE stamps look-ahead from mediaSequenceIndex — correct FE store if a prior take leaked
    // another deck's index onto this Next item (store otherwise wins over the layer).
    const nextItemId = liveNextTarget?.itemId ?? nextRow?.itemId;
    seedLiveSlideIndexFromLayerCurrentIndex(nextItemId, pushedLookAheadLayers);
  }, [
    applyLayers,
    previewRequestKey,
    pushedLookAheadLayers,
    usePushedLookAhead,
    liveNextTarget?.itemId,
    nextRow?.itemId,
  ]);

  /**
   * Simulation (and LIVE HTTP fallback when push has not arrived): load preview layers.
   * LIVE sequential promote / transport: do not HTTP-remint; drop stale layers (spinner covers the frame).
   * Go-back leftover (PPT title, Image frame): remint even if the request key was already stamped.
   */
  useEffect(() => {
    if (usePushedLookAhead) {
      return;
    }
    if (!planFlowId || !previewRequestKey) {
      appliedRequestKeyRef.current = '';
      layersFingerprintRef.current = '';
      setLayers([]);
      setLayersRequestReady(true);
      setContentReady(true);
      return;
    }
    const nextPreviewItemId = nextRecalculating
      ? (nextRow?.itemId ?? liveNextTarget?.itemId)
      : (liveNextTarget?.itemId ?? nextRow?.itemId);
    const paintedLayersMatchNext = Boolean(
      nextPreviewItemId && nextPreviewLayersMatchTarget(layersRef.current, nextPreviewItemId),
    );
    const paintedFitsShowMode = tab !== 'live' || previewLayersFitShowMode(layersRef.current, showMode);
    const showModeChanged =
      tab === 'live' && (appliedShowModeRef.current !== showMode || (paintedLayersMatchNext && !paintedFitsShowMode));
    const httpDecision = resolveLiveNextPreviewHttpDecision({
      sequentialPromotePending,
      skipLiveHttpFallback,
      paintedLayersMatchNext,
      appliedRequestKeyMatches: previewRequestKey === appliedRequestKeyRef.current,
      showModeChanged,
    });
    if (httpDecision !== 'keep-busy') {
      appliedShowModeRef.current = showMode;
    }
    if (httpDecision === 'applied') {
      setLayersRequestReady(true);
      return;
    }
    if (httpDecision === 'keep-busy' && paintedLayersMatchNext) {
      setLayersRequestReady(true);
      setContentReady(true);
      return;
    }
    const cached = layersCacheRef.current.get(previewRequestKey);
    if (cached) {
      applyLayers(previewRequestKey, cached);
      return;
    }
    // Keep a fitting picture of this Next item. A speaker snapshot under a media-only Next must not stay.
    if (!paintedLayersMatchNext || !paintedFitsShowMode) {
      setLayers([]);
      layersFingerprintRef.current = '';
      setContentReady(true);
      setStillFrameResetNonce((n) => n + 1);
    }
    setLayersRequestReady(false);
    if (performMode === 'play') {
      loadPreviewLayersForKey(previewRequestKey);
    }
  }, [
    applyLayers,
    loadPreviewLayersForKey,
    liveNextTarget?.itemId,
    nextRecalculating,
    nextRow?.itemId,
    performMode,
    planFlowId,
    previewRequestKey,
    sequentialPromotePending,
    skipLiveHttpFallback,
    showMode,
    tab,
    usePushedLookAhead,
  ]);

  useEffect(() => {
    if (usePushedLookAhead || performMode === 'play') {
      return;
    }
    if (!planFlowId || !debouncedPreviewRequestKey || debouncedPreviewRequestKey !== previewRequestKey) {
      return;
    }
    if (debouncedPreviewRequestKey === appliedRequestKeyRef.current) {
      setLayersRequestReady(true);
      return;
    }
    loadPreviewLayersForKey(debouncedPreviewRequestKey);
  }, [
    debouncedPreviewRequestKey,
    loadPreviewLayersForKey,
    performMode,
    planFlowId,
    previewRequestKey,
    usePushedLookAhead,
  ]);

  useEffect(() => {
    if (performMode !== 'simulation' || !prefetchRequestKey || layersCacheRef.current.has(prefetchRequestKey)) {
      return;
    }
    if (!layersRequestReady) {
      return;
    }

    void fetchPreviewLayers(prefetchRequestKey)
      .then(warmPreviewLayerMedia)
      .catch(() => {
        // Best effort — a miss just means the normal fetch runs on advance.
      });
  }, [fetchPreviewLayers, layersRequestReady, performMode, prefetchRequestKey]);

  useEffect(() => {
    if (layersRequestReady && layers.length === 0) {
      setContentReady(true);
    }
  }, [layers.length, layersRequestReady]);

  const onReadyChangeRef = useRef(onReadyChange);
  useEffect(() => {
    onReadyChangeRef.current = onReadyChange;
  }, [onReadyChange]);

  useEffect(() => {
    if (nextRecalculating) {
      onReadyChangeRef.current?.(false);
      return;
    }
    if (!nextRow) {
      onReadyChangeRef.current?.(true);
      return;
    }
    onReadyChangeRef.current?.(layersRequestReady && contentReady);
  }, [nextRow, nextRecalculating, layersRequestReady, contentReady]);

  const handleContentReadyChange = useCallback((ready: boolean) => {
    setContentReady((prev) => (prev === ready ? prev : ready));
  }, []);

  const { advanceCue, isPending } = usePerformAdvanceCue({
    flow,
    entityRef,
    performMode,
    liveContextState,
    liveContextQueryUpdatedAt,
  });

  const nextLabel = t('flows.detail.control.nowNext.next', { defaultValue: 'Next media' });
  const advanceLabel = t('flows.detail.perform.transport.nextMedia', { defaultValue: 'Next media' });
  const emptyLabel = t('flows.detail.control.nowNext.noNext', {
    defaultValue: 'No upcoming cue item.',
  });
  const loadingLabel = t('flows.detail.control.nowNext.loading', { defaultValue: 'Loading previews' });

  const nextMeta = useMemo(
    () =>
      nextRow
        ? resolveNextCuePreviewMeta({
            row: nextRow,
            flow,
            slots: flow.slots,
            layers,
            t,
          })
        : null,
    [nextRow, flow, layers, t],
  );

  const slideNavigationScope: PresentationSlideNavigationScope = performMode === 'play' ? 'live' : 'simulation';

  const displayLayers = useMemo(() => {
    if (performMode !== 'play') {
      return layers;
    }
    const liveLayers = resolveOutputPreviewLayers({
      performMode: 'play',
      tab,
      liveContextState,
      displayPlayerIds,
    });
    return attachLiveKitTracksToLookAheadPreviewLayers(layers, liveLayers);
  }, [performMode, layers, tab, liveContextState, displayPlayerIds]);

  // The visible Next is what goes on air on the following take — its video has to be buffered by then.
  useEffect(() => {
    warmPreviewLayerMedia(displayLayers);
  }, [displayLayers]);

  const useLiveKitNextPreview = performMode === 'play' && Boolean(livekitRoomId);

  const liveSlideControlContext = useMemo(() => {
    if (performMode !== 'play') {
      return null;
    }
    const liveContext = flow.liveContext;
    if (liveContext?.isRunning && liveContext.entityType && liveContext.entityId) {
      return { entityType: liveContext.entityType, entityId: liveContext.entityId };
    }
    if (!planFlowId) {
      return null;
    }
    return { entityType: 'Flow' as const, entityId: planFlowId };
  }, [performMode, flow.liveContext, planFlowId]);

  const nextWaitingForPaint =
    performMode !== 'play' && Boolean(nextRow?.itemId) && displayLayers.length === 0 && !layersRequestReady;
  const nextHttpInFlight =
    performMode === 'play' && Boolean(nextRow?.itemId) && !usePushedLookAhead && !layersRequestReady;
  const showNextBusy =
    performMode === 'play'
      ? (usePushedLookAhead
          ? false
          : shouldBlankLiveNextPreview({
              transportBusy,
              sequentialPromotePending,
              nextRecalculating,
              isPending,
              nextHttpInFlight,
            })) ||
        (!viewsReady && !nextRow)
      : nextRecalculating || isPending || nextWaitingForPaint || nextHttpInFlight || (!viewsReady && !nextRow);

  const onLiveNextPaintedRef = useRef(onLiveNextPainted);
  useEffect(() => {
    onLiveNextPaintedRef.current = onLiveNextPainted;
  }, [onLiveNextPainted]);

  useEffect(() => {
    const report = onLiveNextPaintedRef.current;
    if (!report) {
      return;
    }
    const itemId = liveNextTarget?.itemId ?? nextRow?.itemId;
    if (performMode !== 'play' || showNextBusy || !layersRequestReady || !contentReady) {
      report(null);
      return;
    }
    if (!itemId) {
      const cueSettled = Boolean(cueState?.currentStep?.itemId);
      const cueHasNext = Boolean(cueState?.nextItemId);
      report(cueSettled && !cueHasNext ? LIVE_NEXT_PAINTED_NONE : null);
      return;
    }
    if (displayLayers.length === 0) {
      report(null);
      return;
    }
    if (!lookAheadLayersUsableForNextPreview(displayLayers, itemId)) {
      report(null);
      return;
    }
    report(itemId);
  }, [
    contentReady,
    cueState?.currentStep?.itemId,
    cueState?.nextItemId,
    displayLayers,
    layersRequestReady,
    liveNextTarget?.itemId,
    nextRow?.itemId,
    performMode,
    showNextBusy,
  ]);

  const overlay = (
    <>
      {showLabel ? <FlowControlPreviewFrameLabel label={nextLabel} color="success.main" /> : null}
      {!showNextBusy && !nextRecalculating && nextMeta?.isPresentation ? (
        <FlowControlNextStaticPreviewDisclaimer />
      ) : null}
      {!showNextBusy && !nextRecalculating && nextMeta ? <FlowControlNextCueMetaOverlay meta={nextMeta} /> : null}
      {showNextBusy ? null : nextRow ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Tooltip title={advanceLabel}>
            <span style={{ pointerEvents: 'auto' }}>
              <IconButton
                size="large"
                onClick={advanceCue}
                aria-label={advanceLabel}
                sx={{
                  'bgcolor': 'rgba(0,0,0,0.55)',
                  'color': '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                  'boxShadow': '0 2px 10px rgba(0,0,0,0.65)',
                }}
              >
                <PlayArrow sx={{ fontSize: 42 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ) : null}
      {!showNextBusy && !nextRecalculating ? (
        <PerformShowModeOverlay
          flow={flow}
          entityRef={entityRef}
          target="next"
          performMode={performMode}
          liveContextState={liveContextState}
          nextCueIdentity={cueIdentity || undefined}
          lookAheadShowMode={cueState?.lookAheadShowMode}
          nextItemId={cueState?.nextItemId ?? liveNextTarget?.itemId ?? nextRow?.itemId}
        />
      ) : null}
    </>
  );

  return (
    <Box
      onErrorCapture={handlePreviewImageError}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: '100%' }}
    >
      <LiveSlideControlProvider value={liveSlideControlContext}>
        <FlowControlPreviewFrame
          overlay={overlay}
          borderColor={nextRow && !nextRecalculating ? 'success.main' : undefined}
          borderWidth={2}
        >
          {showNextBusy ? (
            <Box
              sx={{
                height: '100%',
                bgcolor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={42} sx={{ color: '#fff' }} aria-label={loadingLabel} />
            </Box>
          ) : !nextRow ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                textAlign: 'center',
              }}
            >
              {nextRecalculating ? null : (
                <Typography variant="body2" color="text.secondary">
                  {emptyLabel}
                </Typography>
              )}
            </Box>
          ) : displayLayers.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                textAlign: 'center',
              }}
            >
              {!layersRequestReady ? (
                <CircularProgress size={32} sx={{ color: '#fff' }} aria-label={loadingLabel} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {nextRow.label ?? nextRow.itemId}
                </Typography>
              )}
            </Box>
          ) : useLiveKitNextPreview && livekitRoomId ? (
            <LiveKitOutputConnector
              livekitRoomId={livekitRoomId}
              layers={displayLayers}
              height="100%"
              slideNavigationScope={slideNavigationScope}
              preferAbsoluteSlideGoto
              sequencePlaybackControls={false}
              liveContextState={liveContextState}
              stillFrame
              stillFrameResetNonce={stillFrameResetNonce}
              onReadyChange={handleContentReadyChange}
            />
          ) : (
            <SimulationOutputPreview
              layers={displayLayers}
              height="100%"
              sequencePlaybackControls={false}
              stillFrame
              stillFrameResetNonce={stillFrameResetNonce}
              slideNavigationScope={slideNavigationScope}
              preferAbsoluteSlideGoto
              onStillFrameReadyChange={handleContentReadyChange}
              // Non-null sentinel: skip simulation playback-complete side effects for this look-ahead preview.
              liveOnAirItemId="__next-cue-preview__"
            />
          )}
        </FlowControlPreviewFrame>
      </LiveSlideControlProvider>
    </Box>
  );
}
